import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, isApiError } from "..";
import { zodParse } from "../zodParse";

// Crosslink rule: schemas here MUST NOT include application_id or any
// vision_/medical_/patient_/proxy_ keys. Aggregate signals only.

// Response status enum (4 values). Drafts collapse to "none" here; the
// distinguishing signal in the response is application_started_at.
const ApplicationStatus = z.enum(["none", "pending", "approved", "rejected"]);

// Filter values include "draft" as a 5th bucket so admins can isolate
// "started but never submitted" rows from "no application at all". The
// backend status filter accepts these 5 values; the response only emits 4.
export const APPLICATION_STATUS_FILTER_VALUES = [
  "none",
  "draft",
  "pending",
  "approved",
  "rejected",
] as const;
export type ApplicationStatusFilter = (typeof APPLICATION_STATUS_FILTER_VALUES)[number];

const AdminAccountSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  locale: z.string(),
  is_admin: z.boolean(),
  is_reviewer: z.boolean(),
  application_status: ApplicationStatus,
  // application_started_at distinguishes a draft (set + status='none') from
  // a never-started account (null + status='none').
  application_started_at: z.string().datetime().nullable(),
  last_review_at: z.string().datetime().nullable(),
});

const ConsentSchema = z.object({
  contact: z.boolean(),
  contact_email: z.boolean(),
  contact_phone: z.boolean(),
  contact_recurring: z.boolean(),
});

// First-step application personas. Values are null when the user has no
// submitted application; for self apps, the proxy block fields are null.
const PatientSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  age_of_majority: z.boolean().nullable(),
});

const ProxySchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  country: z.string().nullable(),
  state: z.string().nullable(),
  date_of_birth: z.string().nullable(),
});

const ApplicantType = z.enum(["self", "proxy"]).nullable();

// Detail schema: omits the dead User columns (address_*, country,
// phone_number, date_of_birth, age_of_majority) — they're never written by
// any user-facing flow, so showing them as "—" was just noise. The real
// personal data flows through the patient/proxy blocks below.
const AdminAccountDetailSchema = AdminAccountSchema.extend({
  auth0_id: z.string(),
  hubspot_contact_id: z.string().nullable(),
  applicant_type: ApplicantType,
  additional_info: z.string().nullable(),
  personal_travel_willing: z.boolean().nullable(),
  patient: PatientSchema,
  proxy: ProxySchema,
  consent: ConsentSchema,
  // Lifecycle timestamps. application_started_at is inherited from the list
  // schema; submitted_at / updated_at are detail-only.
  application_submitted_at: z.string().datetime().nullable(),
  application_updated_at: z.string().datetime().nullable(),
});

const AdminStatsSchema = z.object({
  total_accounts: z.number(),
  total_applications: z.number(),
  pending: z.number(),
  approved: z.number(),
  rejected: z.number(),
});

const PaginationMetaSchema = z.object({
  page: z.number(),
  per_page: z.number(),
  n_pages: z.number(),
  total: z.number(),
});

const AdminAccountsResponseSchema = z.object({
  accounts: z.array(AdminAccountSchema),
  stats: AdminStatsSchema,
  pagination: PaginationMetaSchema,
  // ISO codes present in the data — drives the Country facet options.
  country_options: z.array(z.string()),
});

export type AdminAccount = z.infer<typeof AdminAccountSchema>;
export type AdminAccountDetail = z.infer<typeof AdminAccountDetailSchema>;
export type AdminAccountsResponse = z.infer<typeof AdminAccountsResponseSchema>;
export type AdminAccountConsent = z.infer<typeof ConsentSchema>;
export type ApplicationStatusValue = z.infer<typeof ApplicationStatus>;

export interface AdminAccountsParams {
  filters?: {
    email?: string;
    name?: string;
    is_admin?: boolean;
    is_reviewer?: boolean;
    application_status?: ApplicationStatusFilter[];
    locale?: string[];
    country?: string[];
    travel_willing?: string[];
  };
  page?: number;
  per_page?: number;
  order?: { email?: "asc" | "desc" };
}

const buildQueryParams = ({
  filters,
  page,
  per_page,
  order,
}: AdminAccountsParams): Record<string, unknown> => {
  const query: Record<string, unknown> = {};
  if (filters?.email) query.email = filters.email;
  if (filters?.name) query.name = filters.name;
  if (filters?.is_admin) query.is_admin = "true";
  if (filters?.is_reviewer) query.is_reviewer = "true";
  if (filters?.application_status?.length) {
    query.application_status = filters.application_status;
  }
  if (filters?.locale?.length) query.locale = filters.locale;
  if (filters?.country?.length) query.country = filters.country;
  if (filters?.travel_willing?.length) query.travel_willing = filters.travel_willing;
  if (page && page > 1) query.page = page;
  if (per_page) query.per_page = per_page;
  if (order?.email) {
    query.order_key = "email";
    query.order_direction = order.email;
  }
  return query;
};

async function fetchAdminAccounts(
  params: AdminAccountsParams,
): Promise<AdminAccountsResponse> {
  try {
    const { data } = await api.get("/admin/accounts", {
      params: buildQueryParams(params),
    });
    return zodParse(AdminAccountsResponseSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Admin privileges required.");
      }
      throw new Error(error.response?.data?.message || "Failed to fetch accounts");
    }
    throw error;
  }
}

export function useAdminAccounts(params: AdminAccountsParams = {}) {
  return useQuery({
    queryKey: ["admin", "accounts", params],
    queryFn: () => fetchAdminAccounts(params),
    keepPreviousData: true,
  });
}

async function fetchAdminAccount(id: string): Promise<AdminAccountDetail> {
  try {
    const { data } = await api.get(`/admin/accounts/${id}`);
    return zodParse(AdminAccountDetailSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Admin privileges required.");
      }
      if (error.response?.status === 404) {
        throw new Error("Account not found.");
      }
      throw new Error(error.response?.data?.message || "Failed to fetch account");
    }
    throw error;
  }
}

export function useAdminAccount(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "account", id],
    queryFn: () => fetchAdminAccount(id!),
    enabled: !!id,
  });
}

async function deleteAccountApplication(accountId: string): Promise<void> {
  try {
    await api.delete(`/admin/accounts/${accountId}/application`);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 403) {
        throw new Error("Access denied. Admin privileges required.");
      }
      if (error.response?.status === 404) {
        throw new Error("This account no longer has an application.");
      }
      throw new Error(error.response?.data?.message || "Failed to delete application");
    }
    throw error;
  }
}

// Resets the account to its post-signup state. The lists and the header stats
// both count applications, so they're invalidated alongside the detail view.
export function useDeleteAccountApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => deleteAccountApplication(accountId),
    onSuccess: (_, accountId) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "account", accountId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "applications"] });
    },
  });
}
